const POPUP_TIMEOUT_MS = 30 * 60 * 1000;
const SCROLL_THRESHOLD = 5;

// The bar's height is not a fixed number — icon sizes, label font-size and the
// 8px vertical padding all shift across the 767/399/379/320 breakpoints. Publish
// the real rendered height so bottom-nav-bar.css can park the floating chat
// button exactly on top of the bar.
const publishNavHeight = (bar) => {
  document.documentElement.style.setProperty('--bottom-nav-height', `${bar.offsetHeight}px`);
};

// Swaps the account label to the logged-in wording once a Shopflo SSO session is
// known. The tap itself belongs to the delegated handler in
// shopflo-sso-add-button.js, which survives the innerHTML swap below; only the
// label has to be re-applied by hand after a re-render.
const autoFloLogin = () => {
  const accountLabel = document.querySelector('[data-bottom-nav-bar] [data-account-label]');
  if (!accountLabel) return;

  accountLabel.textContent =
    accountLabel.closest('[data-bottom-nav-bar]').dataset.accountLoggedInLabel ?? '';
};

// getFloAccessToken only exists once the interaction-gated bundle has run, which
// is always after DOMContentLoaded — so the token has to be re-checked on load.
const syncFloAccountLabel = () => {
  if (typeof getFloAccessToken === 'function' && getFloAccessToken()) autoFloLogin();
};

document.addEventListener('DOMContentLoaded', () => {
  const bar = document.querySelector('[data-bottom-nav-bar]');
  if (!bar) return;

  const cartDrawer = document.querySelector('cart-drawer');
  let cartItemCount = parseInt(bar.dataset.cartCountValue ?? '0', 10);

  // Observed rather than measured once: the bar is display:none above 767px, so
  // the height only becomes real after a resize back under the breakpoint.
  publishNavHeight(bar);
  new ResizeObserver(() => publishNavHeight(bar)).observe(bar);

  syncFloAccountLabel();

  const lastClosedTime = localStorage.getItem('popupClosedTime');
  if (lastClosedTime && Date.now() - Number(lastClosedTime) < POPUP_TIMEOUT_MS) return;

  let scrollCount = 0;
  let popupShown = false;

  const getPopup = () => bar.querySelector('[data-bottom-nav-popup]');

  const showPopup = () => {
    const popup = getPopup();
    if (!popup) return;
    popup.classList.add('bottom-nav-bar__popup--visible');
    popupShown = true;
  };

  const hidePopup = () => {
    const popup = getPopup();
    if (!popup) return;
    popup.classList.remove('bottom-nav-bar__popup--visible');
  };

  const handleScroll = () => {
    if (popupShown || cartItemCount === 0) return;
    scrollCount++;
    if (scrollCount >= SCROLL_THRESHOLD) {
      showPopup();
      window.removeEventListener('scroll', handleScroll);
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Delegated on `bar` (not bound directly to the close/footer buttons) so the
  // listeners keep working after refreshFromCart() replaces bar's innerHTML.
  bar.addEventListener('click', (event) => {
    if (event.target.closest('[data-popup-close]')) {
      hidePopup();
      window.removeEventListener('scroll', handleScroll);
      localStorage.setItem('popupClosedTime', String(Date.now()));
      return;
    }
    if (event.target.closest('[data-popup-footer]')) {
      cartDrawer?.open();
    }
  });

  // Cart changes (add/update via AJAX) never reload the page, so the gift-tier
  // text baked into this section's HTML at page-load would otherwise go stale
  // forever. Re-fetch this section on every cart:rendered and patch it in.
  const sectionId = bar.dataset.sectionId;
  const refreshFromCart = async () => {
    if (!sectionId) return;
    try {
      const res = await fetch(`/?sections=${sectionId}`);
      const data = await res.json();
      const html = data?.[sectionId];
      if (!html) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const freshBar = parsed.querySelector('[data-bottom-nav-bar]');
      if (!freshBar) return;

      const wasVisible = getPopup()?.classList.contains('bottom-nav-bar__popup--visible');

      cartItemCount = parseInt(freshBar.dataset.cartCountValue ?? '0', 10);
      bar.dataset.cartCountValue = String(cartItemCount);
      bar.innerHTML = freshBar.innerHTML;

      // The fresh markup is rendered from `customer`, which is nil for a
      // Shopflo-only session — so an add-to-cart would otherwise revert the
      // label to "Log in" for a visitor who is signed in.
      syncFloAccountLabel();

      if (wasVisible) showPopup();
    } catch (_) {
    }
  };

  document.addEventListener('cart:rendered', refreshFromCart);
});

window.addEventListener('theme:shopflo:loaded', syncFloAccountLabel);

window.addEventListener('user-loggedin', (event) => {
  const { token } = event?.detail ?? {};
  if (token) autoFloLogin();
});
